import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import debounce from 'lodash/debounce';

// @mui material components
import { Grid, Card } from '@mui/material';
import { toast } from 'react-toastify';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';

// Material Dashboard 2 React components
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';

// Material Dashboard 2 React example components
import DataTable from 'examples/Tables/DataTable';

import {
  getPropertyType,
  addPropertyType,
  updatePropertyType,
  deletePropertyType,
  resetPropertyTypeList,
} from 'store/reducers/propertyTypeSlice';
import AddPropertyType from './components/AddPropertyType';

import ActionsPopover from '../../components/ActionsPopover/ActionsPopover';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import ImageComponent from '../../components/ImageComponent/ImageComponent';
import { propertyTypesColumns } from 'constants/columns.constants';

import noProperty from '../../assets/images/no-image.png';

const ManagePropertyType = () => {
  //   const { columns, rows } = authorsTableData();

  const [isLoading, setLoading] = useState(false);

  const dispatch = useDispatch();

  const { propertyTypeList } = useSelector((state) => state.propertyType);

  const [selectedPropertyType, setSelectedPropertyType] = useState(null);
  const [isEditProperty, setEditProperty] = useState(false);

  const [openModal, setOpenModal] = useState(false);
  const [isDeleteModal, setDeleteModal] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [sortBy, setSortBy] = useState({
    id: 'id',
    desc: true,
  });
  const [searchTerm, setSearchTerm] = useState('');

  // useEffect(() => {
  //   return () => {
  //     debounceFn.cancel();
  //   };
  // }, [debounceFn]);

  const onCloseModal = () => {
    setSelectedPropertyType(null);
    setEditProperty(false);
    setOpenModal(false);
    setDeleteModal(false);
  };

  const columns = useMemo(() => {
    return propertyTypesColumns;
  }, []);

  const rows =
    propertyTypeList?.propertyTypesData?.length > 0
      ? propertyTypeList?.propertyTypesData?.map((propertyType) => {
          return {
            id: propertyType?.propertyTypeId,
            name: propertyType?.name ?? 'NA',
            image: (
              <ImageComponent
                imgStyles={{
                  height: '32px',
                  width: '32px',
                  objectFit: 'contain',
                  margin: '0',
                  verticalAlign: 'bottom',
                  borderRadius: '0',
                }}
                fallbackImg={noProperty}
                imageUrl={propertyType?.imageUrl ?? ''}
                spinnerSize={30}
              />
            ),
            propertyCount: propertyType?.propertyCount ?? 0,
            actions: (
              <ActionsPopover
                editTitle={'Edit Property Type'}
                deleteTitle={'Delete Property Type'}
                onEditClick={() => {
                  setSelectedPropertyType(propertyType);
                  setEditProperty(true);
                  setOpenModal(true);
                }}
                onDeleteClick={() => {
                  setSelectedPropertyType(propertyType);
                  setDeleteModal(true);
                }}
              />
            ),
          };
        })
      : [];

  async function fetchPropertyTypes() {
    try {
      setLoading(true);
      await dispatch(
        getPropertyType({
          page: currentPage + 1,
          limit: perPage,
          sortField: sortBy?.id ?? 'id',
          sortType: sortBy?.desc && sortBy?.desc ? 'DESC' : 'ASC',
          search: searchTerm,
        })
      );
    } catch (error) {
      console.log({ error });
    } finally {
      setLoading(false);
    }
  }

  const onCreatePropertyType = async (propertyTypeName, propertyImage) => {
    try {
      await dispatch(
        addPropertyType({
          name: propertyTypeName,
          image: propertyImage,
        })
      );
      onCloseModal();
      if (currentPage === 0) {
        fetchPropertyTypes();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message ?? 'Something went wrong!');
    }
  };

  const onUpdatePropertyType = async (
    propertyId,
    propertyTypeName,
    propertyImage
  ) => {
    try {
      await dispatch(
        updatePropertyType({
          id: propertyId,
          body: {
            name: propertyTypeName,
            image: propertyImage,
          },
        })
      );
      onCloseModal();
      if (currentPage === 0) {
        fetchPropertyTypes();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message ?? 'Something went wrong!');
    }
  };

  const onDeletePropertyType = async () => {
    try {
      await dispatch(deletePropertyType(selectedPropertyType?.propertyTypeId));
      if (currentPage === 0) {
        fetchPropertyTypes();
      } else {
        setCurrentPage(0);
      }
    } catch (error) {
      console.log({ error });
    } finally {
      onCloseModal();
    }
  };

  const onFetchDataDebounced = debounce(fetchPropertyTypes, 0);

  // useEffect(() => {
  //   setCurrentPage(0);
  // }, [perPage]);

  useEffect(() => {
    onFetchDataDebounced();

    return () => {
      onFetchDataDebounced.cancel();
    };
  }, [currentPage, sortBy, searchTerm, perPage]);

  useEffect(() => {
    return () => {
      dispatch(resetPropertyTypeList());
    };
  }, []);

  return (
    <MDBox pt={6} pb={3}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={3}
              px={2}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                Property Types
              </MDTypography>
              <MDButton
                variant="outlined"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => setOpenModal(true)}
              >
                Add Property Type
              </MDButton>
            </MDBox>
            <MDBox
              pt={3}
              sx={{
                position: 'relative',
                paddingTop: '85px',
                paddingBottom: {
                  xs: '150px',
                  sm: '95px',
                },
              }}
            >
              <DataTable
                table={{ columns, rows }}
                isSorted={true}
                entriesPerPage={{ defaultValue: perPage }}
                showTotalEntries
                canSearch
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                perPage={perPage}
                setPerPage={setPerPage}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
                totalPage={propertyTypeList?.totalPage}
                totalRecords={propertyTypeList?.totalCount}
                hiddenColumns={['id']}
                queryPageSortBy={[sortBy]}
                setSortBy={setSortBy}
                isLoading={isLoading}
              />
            </MDBox>
            {openModal && (
              <AddPropertyType
                openModal={openModal}
                onCloseModal={onCloseModal}
                onCreatePropertyType={onCreatePropertyType}
                onUpdatePropertyType={onUpdatePropertyType}
                isEdit={isEditProperty}
                selectedPropertyType={selectedPropertyType}
              />
            )}
            {isDeleteModal && (
              <ConfirmModal
                open={isDeleteModal}
                onCloseModal={onCloseModal}
                modalText="Are you sure you want to delete this property type?"
                okayText="Yes, delete"
                cancelText="No, cancel"
                onOkayClick={onDeletePropertyType}
              />
            )}
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
};

export default ManagePropertyType;
